/**
 * Global store for tracking active generation tasks.
 * Persists to localStorage so tasks survive page refresh.
 * Uses a simple pub/sub pattern for React integration.
 */

export interface GenerationTaskResults {
  briefText?: string;
  referenceImages?: string[];
  referenceThumbnails?: string[];
  searchQueriesUsed?: string[];
}

export interface GenerationTask {
  id: string;
  entityName: string;
  entityId: string;
  worldId: string;
  /** "brief" | "image" | "search" */
  phase: "search" | "brief" | "image";
  status: "running" | "done" | "error";
  detail?: string;
  provider?: string;
  query?: string;
  startedAt: number;
  completedAt?: number;
  error?: string;
  results?: GenerationTaskResults;
}

const STORAGE_KEY = "active_generation_tasks";
const STALE_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes — auto-expire stale tasks

type Listener = () => void;
const listeners = new Set<Listener>();

function notify() {
  listeners.forEach((l) => l());
}

function loadTasks(): GenerationTask[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const tasks: GenerationTask[] = JSON.parse(raw);
    // Purge completed/stale tasks
    const now = Date.now();
    return tasks.filter((t) => {
      if (t.status === "done" || t.status === "error") {
        // Keep tasks with results for 10 minutes so dialog can restore them
        const retention = t.results ? 10 * 60_000 : 30_000;
        return (t.completedAt || t.startedAt) > now - retention;
      }
      // Running tasks older than threshold are considered stale/crashed
      if (now - t.startedAt > STALE_THRESHOLD_MS) return false;
      return true;
    });
  } catch {
    return [];
  }
}

function saveTasks(tasks: GenerationTask[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch {}
}

export function getGenerationTasks(): GenerationTask[] {
  return loadTasks();
}

export function getRunningTasks(): GenerationTask[] {
  return loadTasks().filter((t) => t.status === "running");
}

export function addGenerationTask(task: Omit<GenerationTask, "startedAt" | "status">): GenerationTask {
  const fullTask: GenerationTask = { ...task, status: "running", startedAt: Date.now() };
  const tasks = loadTasks();
  // Replace existing task with same id
  const idx = tasks.findIndex((t) => t.id === fullTask.id);
  if (idx >= 0) tasks[idx] = fullTask;
  else tasks.push(fullTask);
  saveTasks(tasks);
  notify();
  return fullTask;
}

export function updateGenerationTask(id: string, update: Partial<GenerationTask>) {
  const tasks = loadTasks();
  const idx = tasks.findIndex((t) => t.id === id);
  if (idx >= 0) {
    tasks[idx] = { ...tasks[idx], ...update };
    saveTasks(tasks);
    notify();
  }
}

export function completeGenerationTask(id: string, error?: string) {
  const tasks = loadTasks();
  const idx = tasks.findIndex((t) => t.id === id);
  if (idx >= 0) {
    tasks[idx] = {
      ...tasks[idx],
      status: error ? "error" : "done",
      completedAt: Date.now(),
      error,
    };
    saveTasks(tasks);
    notify();
    // Auto-remove: tasks with cached results stay 10min, others 30s
    const hasResults = !!tasks[idx].results;
    setTimeout(() => {
      removeGenerationTask(id);
    }, hasResults ? 10 * 60_000 : 30_000);
  }
}

export function removeGenerationTask(id: string) {
  const tasks = loadTasks().filter((t) => t.id !== id);
  saveTasks(tasks);
  notify();
}

export function clearAllTasks() {
  saveTasks([]);
  notify();
}

/** Subscribe to task changes. Returns unsubscribe function. */
export function subscribeToTasks(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
